// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import { FormGroup, Intent, Classes, Button } from '@blueprintjs/core';
import classNames from 'classnames';
import { CellType } from '@/constants';
import { FSelect } from '@/components';
import { useQueryTenant } from '@/hooks/useQueryRequest';
import useApiRequest from '@/hooks/useRequest';
import { useContact } from '@/hooks/query';

export default function TransactionListFieldCell({
  column: { id },
  row: { index, original },
  cell: { value },
  payload: { updateData, updateRow, errors },
}) {
  const apiRequest = useApiRequest();
  const contactId = original?.contact_id;
  const contactType = original?.contact_type; // This is contact_service ('customer' or 'vendor')
  const existingTransactionId = original?.transaction_id;
  const existingTransactionType = original?.transaction_type;

  // If we have a contact ID but no contact type, fetch the contact to determine the type
  // This is crucial for determining whether to fetch invoices or bills
  const { data: fetchedContact } = useContact(
    contactId,
    {
      enabled: !!contactId && !contactType,
    }
  );

  // Determine the effective contact type (from original data or fetched contact)
  const effectiveContactType = contactType || fetchedContact?.contact_service;

  // Determine if we should fetch invoices or bills based on contact type
  const shouldFetchInvoices = effectiveContactType === 'customer' && !!contactId;
  const shouldFetchBills = effectiveContactType === 'vendor' && !!contactId;

  // Debug logging
  console.log('TransactionListFieldCell:', {
    'original object': original,
    contactId,
    contactType,
    effectiveContactType,
    fetchedContact,
    shouldFetchInvoices,
    shouldFetchBills,
  });

  // Fetch invoices for customers
  const { data: invoicesData, isLoading: isLoadingInvoices } = useQueryTenant(
    ['invoices-autocomplete', contactId],
    () => {
      console.log('Fetching invoices for customer:', contactId);
      return apiRequest.get('sales/invoices/autocomplete', {
        params: { customer_id: contactId, limit: 100 },
      });
    },
    {
      enabled: shouldFetchInvoices,
      select: (res) => {
        console.log('Invoices response:', res.data);
        return res.data.invoices || [];
      },
    }
  );

  // Fetch bills for vendors
  const { data: billsData, isLoading: isLoadingBills } = useQueryTenant(
    ['bills-autocomplete', contactId],
    () => {
      console.log('Fetching bills for vendor:', contactId);
      return apiRequest.get('purchases/bills/autocomplete', {
        params: { vendor_id: contactId, limit: 100 },
      });
    },
    {
      enabled: shouldFetchBills,
      select: (res) => {
        console.log('Bills response:', res.data);
        return res.data.bills || [];
      },
    }
  );

  // Fetch the specific transaction if there's a transaction_id but contact hasn't loaded yet
  // This handles the case when editing an existing entry with a transaction reference
  // We wait for contact to be fetched first to determine the transaction type if needed
  const shouldFetchExistingInvoice =
    !!existingTransactionId &&
    existingTransactionType === 'SaleInvoice' &&
    !shouldFetchInvoices;

  const shouldFetchExistingBill =
    !!existingTransactionId &&
    existingTransactionType === 'Bill' &&
    !shouldFetchBills;

  const { data: existingInvoice } = useQueryTenant(
    ['invoice', existingTransactionId],
    () => {
      console.log('Fetching existing invoice:', existingTransactionId);
      return apiRequest.get(`sales/invoices/${existingTransactionId}`);
    },
    {
      enabled: shouldFetchExistingInvoice,
      select: (res) => res.data.invoice,
    }
  );

  const { data: existingBill } = useQueryTenant(
    ['bill', existingTransactionId],
    () => {
      console.log('Fetching existing bill:', existingTransactionId);
      return apiRequest.get(`purchases/bills/${existingTransactionId}`);
    },
    {
      enabled: shouldFetchExistingBill,
      select: (res) => res.data.bill,
    }
  );

  // Prepare items and transaction type based on contact type
  const { items, transactionType } = useMemo(() => {
    console.log('Preparing items:', {
      shouldFetchInvoices,
      shouldFetchBills,
      invoicesData,
      billsData,
      existingInvoice,
      existingBill,
    });

    if (shouldFetchInvoices && invoicesData) {
      console.log('Using invoices:', invoicesData);
      // Format the items for display
      const formattedInvoices = invoicesData.map(invoice => ({
        ...invoice,
        displayText: `${invoice.invoice_no || 'N/A'} - ${invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'No Date'} - ${invoice.balance || 0}`,
      }));
      return {
        items: formattedInvoices,
        transactionType: 'SaleInvoice',
      };
    }
    if (shouldFetchBills && billsData) {
      console.log('Using bills:', billsData);
      // Format the items for display
      const formattedBills = billsData.map(bill => ({
        ...bill,
        displayText: `${bill.bill_number || 'N/A'} - ${bill.bill_date ? new Date(bill.bill_date).toLocaleDateString() : 'No Date'} - ${bill.amount || 0}`,
      }));
      return {
        items: formattedBills,
        transactionType: 'Bill',
      };
    }

    // If we have an existing transaction but no contact selected (editing mode)
    if (existingInvoice && existingTransactionType === 'SaleInvoice') {
      console.log('Using existing invoice:', existingInvoice);
      return {
        items: [{
          ...existingInvoice,
          displayText: `${existingInvoice.invoice_no || 'N/A'} - ${existingInvoice.invoice_date ? new Date(existingInvoice.invoice_date).toLocaleDateString() : 'No Date'} - ${existingInvoice.balance || 0}`,
        }],
        transactionType: 'SaleInvoice',
      };
    }

    if (existingBill && existingTransactionType === 'Bill') {
      console.log('Using existing bill:', existingBill);
      return {
        items: [{
          ...existingBill,
          displayText: `${existingBill.bill_number || 'N/A'} - ${existingBill.bill_date ? new Date(existingBill.bill_date).toLocaleDateString() : 'No Date'} - ${existingBill.amount || 0}`,
        }],
        transactionType: 'Bill',
      };
    }

    console.log('No items available');
    return { items: [], transactionType: existingTransactionType || null };
  }, [shouldFetchInvoices, shouldFetchBills, invoicesData, billsData, existingInvoice, existingBill, existingTransactionType]);

  const handleTransactionChange = useCallback(
    (transaction) => {
      if (transaction) {
        if (updateRow) {
          updateRow(index, {
            transaction_id: transaction.id,
            transaction_type: transactionType,
          });
        } else {
          updateData(index, 'transaction_id', transaction.id);
          updateData(index, 'transaction_type', transactionType);
        }
      } else {
        if (updateRow) {
          updateRow(index, {
            transaction_id: '',
            transaction_type: '',
          });
        } else {
          updateData(index, 'transaction_id', '');
          updateData(index, 'transaction_type', '');
        }
      }
    },
    [updateData, updateRow, index, transactionType]
  );

  // Handle clearing the transaction selection
  const handleClearTransaction = useCallback(
    (e) => {
      e.stopPropagation();
      handleTransactionChange(null);
    },
    [handleTransactionChange],
  );

  const error = errors?.[index]?.[id];

  // Determine the placeholder text based on contact type
  const placeholder = useMemo(() => {
    if (!contactId && !existingTransactionId) {
      return 'Select a contact first';
    }
    if (effectiveContactType === 'customer') {
      return 'Select Invoice';
    }
    if (effectiveContactType === 'vendor') {
      return 'Select Purchase';
    }
    return 'Select Transaction';
  }, [contactId, effectiveContactType, existingTransactionId]);

  // Find the selected item
  const selectedItem = useMemo(() => {
    if (!original?.transaction_id || !items.length) return null;
    return items.find((item) => item.id === original.transaction_id);
  }, [original?.transaction_id, items]);

  // Disable the field only if there's no contact and no existing transaction
  const isDisabled = !contactId && !existingTransactionId;

  return (
    <FormGroup
      intent={error ? Intent.DANGER : null}
      className={classNames(
        'form-group--select-list',
        'form-group--transaction-list',
        Classes.FILL
      )}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
        {selectedItem && !isDisabled && (
          <Button
            icon="cross"
            minimal
            small
            intent={Intent.NONE}
            onClick={handleClearTransaction}
            style={{
              flexShrink: 0,
              padding: '3px',
              height: '24px',
              minHeight: '24px',
              minWidth: '24px',
              width: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Clear selection"
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <FSelect
            name={`entries[${index}].transaction_id`}
            items={items}
            valueAccessor="id"
            textAccessor="displayText"
            value={selectedItem}
            onItemSelect={handleTransactionChange}
            placeholder={placeholder}
            disabled={isDisabled}
            popoverProps={{ minimal: true, usePortal: true }}
          />
        </div>
      </div>
    </FormGroup>
  );
}

TransactionListFieldCell.cellType = CellType.Field;

